import { useState } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import BEM from '../bem';
import { User, GroupTypes } from '../../types';
import { ReactComponent as AddMemberSvg } from '../images/add-member.svg';
import { ReactComponent as AddSuccessSvg } from '../images/add-success.svg';
import { ReactComponent as AddLoadingSvg } from '../images/add-loading.svg';
import { mobxInjectSelect } from '../utils';
import { type ForwardedEntity } from '../utils/autoForward';
import { EntityAvatar, UserName, UserTitleDepartment } from './index';

const classes = BEM.with('DelegationMemberInfo');

type AddMemberState = 'none' | 'can-add' | 'already-added';

function filterValidEntities(autoForward?: (ForwardedEntity | undefined)[]): ForwardedEntity[] {
  if (!autoForward) return [];
  return autoForward.filter((entity): entity is ForwardedEntity => Boolean(entity));
}

function isTeamRecipient(autoForward?: (ForwardedEntity | undefined)[]): boolean {
  const validEntities = filterValidEntities(autoForward);
  return validEntities.length === 1 && validEntities[0]?.entityType === 'team';
}

function getAutoForwardDisplayName(autoForward?: (ForwardedEntity | undefined)[]): string {
  const validEntities = filterValidEntities(autoForward);
  if (validEntities.length > 1) {
    return 'Multiple recipients';
  }
  return validEntities[0]?.displayName || '';
}

const getAddMemberState = (
  memberIds: string[],
  isTeamMember = false,
  teamMemberId?: string,
  autoForward?: (ForwardedEntity | undefined)[],
  groupMembers?: User[]
): { state: AddMemberState; receiverId?: string } => {
  if (isTeamMember && teamMemberId) {
    const isAlreadyMember = memberIds.includes(teamMemberId);
    return {
      state: isAlreadyMember ? 'already-added' : 'can-add',
      receiverId: teamMemberId,
    };
  }

  if (!autoForward) return { state: 'none' };

  const validEntities = filterValidEntities(autoForward);
  const hasMultipleRecipients = validEntities.length > 1;
  const isTeam = isTeamRecipient(autoForward);

  if (hasMultipleRecipients || isTeam) return { state: 'none' };

  const forwardedEntity = validEntities[0];
  if (!forwardedEntity) return { state: 'none' };

  let receiverIdForComparison = forwardedEntity.token;
  let receiverId = forwardedEntity.token;
  if (forwardedEntity.entityType === 'role' && groupMembers) {
    const matchingMember = groupMembers.find(
      (m) =>
        m.isRoleBot &&
        (m.botRole?.id === forwardedEntity.token || m.botRoleId === forwardedEntity.token)
    );
    if (matchingMember) {
      receiverIdForComparison = matchingMember.botUser?.id || matchingMember.id;
      receiverId = receiverIdForComparison;
    }
  }

  const isAlreadyMember = receiverIdForComparison && memberIds.includes(receiverIdForComparison);

  return {
    state: isAlreadyMember ? 'already-added' : 'can-add',
    receiverId,
  };
};

const AutoForwardText = ({
  autoForward,
  member,
  memberIds,
  addMembers,
  groupId,
  groupType,
  organizationId,
  showAutoForwardRecipientsPane,
  isInInfoPane,
  isTeamMember,
  groupMembers,
  logGainsightAnalytics,
}: {
  autoForward?: (ForwardedEntity | undefined)[];
  member?: User;
  memberIds?: string[];
  addMembers?: (groupId: string, memberIds: string[]) => Promise<void>;
  groupId?: string;
  groupType?: string;
  organizationId?: string;
  showAutoForwardRecipientsPane?: (params: {
    autoForwardRecipients: (ForwardedEntity | undefined)[];
    user: User;
    groupId?: string;
    groupType?: string;
    organizationId: string;
  }) => void;
  isInInfoPane?: boolean;
  isTeamMember?: boolean;
  groupMembers?: User[];
  logGainsightAnalytics?: (params: {
    tracker: { name: string; props?: Record<string, unknown> };
  }) => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const shouldShowAddMemberIcons =
    isInInfoPane &&
    member?.id &&
    memberIds &&
    groupId &&
    groupType !== GroupTypes.ROLE_P2P &&
    (groupType === GroupTypes.GROUP || groupType === GroupTypes.FORUM);

  if ((!autoForward || autoForward.length === 0) && !shouldShowAddMemberIcons) {
    return null;
  }

  const validEntities = filterValidEntities(autoForward);
  const hasMultipleRecipients = validEntities.length > 1;
  const isTeam = isTeamRecipient(autoForward);
  const teamMemberId = isTeamMember && member ? member.botUser?.id || member.id : undefined;
  const addMemberInfo = getAddMemberState(
    memberIds || [],
    isTeamMember,
    teamMemberId,
    autoForward,
    groupMembers
  );

  const handleAddMember = async () => {
    const targetMemberId = addMemberInfo.receiverId || member?.botUser?.id || member?.id;
    if (!groupId || !targetMemberId || !addMembers) return;

    const isRoleRecipient = Boolean(member?.isRoleBot || member?.botRole);

    logGainsightAnalytics?.({
      tracker: {
        name: 'Inbox | Messenger - Add Auto-Forward Recipient',
        props: {
          Source: isInInfoPane ? 'ViewMultiple' : 'ConversationBanner',
          AfRecipientType: isRoleRecipient ? 'Role' : 'Individual',
        },
      },
    });

    setIsLoading(true);
    try {
      await addMembers(groupId, [targetMemberId]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewRecipients = () => {
    if (
      showAutoForwardRecipientsPane &&
      autoForward &&
      (isTeam || hasMultipleRecipients) &&
      organizationId &&
      member?.displayName
    ) {
      logGainsightAnalytics?.({
        tracker: {
          name: 'Inbox | Messenger - View All Auto-Forward Recipients',
        },
      });

      showAutoForwardRecipientsPane({
        autoForwardRecipients: validEntities,
        user: member,
        groupId,
        groupType,
        organizationId,
      });
    }
  };

  let canAddMemberDirectly = false;
  let isAlreadyMemberDirectly = false;
  if (shouldShowAddMemberIcons && (!autoForward || autoForward.length === 0) && member?.id) {
    const memberIdToCheck = member.botUser?.id || member.id;
    isAlreadyMemberDirectly = memberIds.includes(memberIdToCheck);
    canAddMemberDirectly = !isAlreadyMemberDirectly;
  }

  const autoForwardName = getAutoForwardDisplayName(autoForward);

  return (
    <div className={classes('auto-forward-text', { inInfoPane: isInInfoPane })}>
      <div className={classes('auto-forward-text-container')}>
        {!isInInfoPane && validEntities.length > 0 && (
          <span data-test-id="auto-forwarding-to-text">Auto-forwarding to: {autoForwardName}</span>
        )}
        {(hasMultipleRecipients || isTeam) && !isInInfoPane && (
          <button
            className={classes('auto-forward-text-view')}
            data-test-id="view-recipients-button"
            onClick={handleViewRecipients}
            type="button"
            disabled={!showAutoForwardRecipientsPane || !organizationId}
          >
            View
          </button>
        )}
        {isLoading && (
          <AddLoadingSvg
            className={classnames(
              classes('auto-forward-text-icon'),
              classes('auto-forward-text-icon--spinning')
            )}
            data-test-id="add-loading-icon"
          />
        )}
        {!isLoading &&
          (addMemberInfo.state === 'can-add' || canAddMemberDirectly) &&
          groupType !== GroupTypes.ROLE_P2P &&
          (groupType === GroupTypes.GROUP || groupType === GroupTypes.FORUM) && (
            <AddMemberSvg
              className={classnames(
                classes('auto-forward-text-icon'),
                classes('auto-forward-text-icon--pointer')
              )}
              data-test-id="add-member-button"
              onClick={handleAddMember}
            />
          )}
        {!isLoading && (addMemberInfo.state === 'already-added' || isAlreadyMemberDirectly) && (
          <AddSuccessSvg
            className={classes('auto-forward-text-icon')}
            data-test-id="add-success-icon"
          />
        )}
      </div>
    </div>
  );
};

type DelegationMemberInfoOwnProps = {
  member: User;
  autoForward?: (ForwardedEntity | undefined)[];
  memberIds?: string[];
  groupId?: string;
  groupType?: string;
  isInInfoPane?: boolean;
  isTeamMember?: boolean;
  smallOwnerName?: boolean;
  groupMembers?: User[];
};

type DelegationMemberInfoMobxProps = {
  addMembers: (groupId: string, memberIds: string[]) => Promise<void>;
  composeToEntityV2: (entity: User) => void;
  currentOrganizationId: string;
  currentUser: User;
  currentUserId: string;
  isProviderNetwork: boolean;
  isUserDndAfEnabledForCurrentOrg: (user: User) => boolean;
  logGainsightAnalytics: (params: {
    tracker: { name: string; props?: Record<string, unknown> };
  }) => void;
  showAutoForwardRecipientsPane: (params: {
    autoForwardRecipients: (ForwardedEntity | undefined)[];
    user: User;
    groupId?: string;
    groupType?: string;
    organizationId: string;
  }) => void;
};

type DelegationMemberInfoProps = DelegationMemberInfoOwnProps & DelegationMemberInfoMobxProps;

function DelegationMemberInfoComponent({
  member,
  currentUser,
  currentUserId,
  currentOrganizationId,
  isProviderNetwork,
  isUserDndAfEnabledForCurrentOrg,
  composeToEntityV2,
  autoForward,
  memberIds,
  addMembers,
  groupId,
  groupType,
  logGainsightAnalytics,
  showAutoForwardRecipientsPane,
  isInInfoPane,
  isTeamMember,
  smallOwnerName,
  groupMembers,
}: DelegationMemberInfoProps) {
  const isCurrentUser = member.id === currentUserId;
  const isRoleBot = currentUser?.roles?.some((role) => role.botUserId === member.id);
  const isMemberClickable = isProviderNetwork && !isCurrentUser && !isRoleBot;

  const handleMemberClick = () => {
    if (isMemberClickable && composeToEntityV2) {
      composeToEntityV2(member);
    }
  };

  const isDndAfEnabled = isUserDndAfEnabledForCurrentOrg(member);

  return (
    <div className={classes()}>
      <div className={classes('member-info-container')}>
        <div
          className={classes('member-info', {
            clickable: isMemberClickable,
            inInfoPane: isInInfoPane,
          })}
          onClick={handleMemberClick}
          data-test-id="delegation-member-info"
        >
          <EntityAvatar
            entity={member.isRoleBot && member.botRole ? member.botRole : member}
            size={30}
            showPresenceIndicator
            showDndAfStatus={isDndAfEnabled}
          />
          <div className={classes('member-details', { inInfoPane: isInInfoPane })}>
            <UserName
              className={classes('member-name', {
                dndAf: isDndAfEnabled,
              })}
              includeRoleOwner
              isCurrentUser={isCurrentUser}
              user={member}
              smallOwnerName={smallOwnerName}
              showOnDutyPrefix={true}
            />
            {member.dndText && (
              <div className={classes('member-dnd-text', { inInfoPane: isInInfoPane })}>
                {member.dndText}
              </div>
            )}
            <UserTitleDepartment className={classes('member-title')} user={member} />
          </div>
        </div>
        <AutoForwardText
          autoForward={autoForward}
          member={member}
          memberIds={memberIds}
          addMembers={addMembers}
          groupId={groupId}
          groupType={groupType}
          organizationId={currentOrganizationId}
          showAutoForwardRecipientsPane={showAutoForwardRecipientsPane}
          isInInfoPane={isInInfoPane}
          isTeamMember={isTeamMember}
          groupMembers={groupMembers}
          logGainsightAnalytics={logGainsightAnalytics}
        />
      </div>
    </div>
  );
}

export default mobxInjectSelect<DelegationMemberInfoOwnProps, DelegationMemberInfoMobxProps>({
  composeMessageStore: ['composeToEntityV2'],
  groupStore: ['addMembers'],
  messengerStore: ['currentOrganizationId'],
  networkStore: ['isProviderNetwork'],
  sessionStore: ['currentUser', 'currentUserId'],
  trackerStore: ['logGainsightAnalytics'],
  userStore: ['isUserDndAfEnabledForCurrentOrg', 'showAutoForwardRecipientsPane'],
})(observer(DelegationMemberInfoComponent));
